import {FC, Suspense} from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  styled,
  TextField,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  useGetContractsCidDocumentsRefresh,
  usePutContractsCidDocumentUpdateMutation,
} from '../../../../global/services/contracts';
import {Form, useParams} from 'react-router-dom';
import {toNumber} from 'lodash';
import {
  documentUpdateModalClose,
  useContractDocumentUpdateModalStore,
} from '../../stores/contract-document-update-modal.store';
import {useForm} from 'react-hook-form';

interface FormValues {
  name: string;
  description: string;
}

export const UpdateDocumentModal: FC = () => {
  const updatableId = useContractDocumentUpdateModalStore((s) => s.updatableId);

  return (
    <StyledDialog fullWidth maxWidth="xs" open={!!updatableId}>
      <DialogTitle>Редактировать название</DialogTitle>
      <Suspense fallback={<StyledCircularProgress />}>
        {updatableId && <UpdateDocumentForm updatableId={updatableId} />}
      </Suspense>
    </StyledDialog>
  );
};

const UpdateDocumentForm = ({updatableId}: {updatableId: string}) => {
  const {id: cid} = useParams();
  const {fileName: name, description} = useContractDocumentUpdateModalStore();
  const {mutate, loading: mutateLoading} = usePutContractsCidDocumentUpdateMutation();
  const refreshDocuments = useGetContractsCidDocumentsRefresh();
  const formApi = useForm<FormValues>({defaultValues: {name, description}});
  const {errors} = formApi.formState;
  const loading = mutateLoading;

  const afterMutationCallback = () => {
    documentUpdateModalClose();
    refreshDocuments();
  };

  const handleDocumentEdit = async (values: FormValues) => {
    if (!cid) return;
    await mutate(
      [toNumber(cid), {docId: updatableId, newName: values.name, newDescription: values.description}],
      afterMutationCallback,
    );
  };

  return (
    <Form onSubmit={formApi.handleSubmit(handleDocumentEdit)}>
      <StyledDialogContent>
        <TextField
          label="Название"
          variant="standard"
          {...formApi.register('name', {required: 'Обязательно для заполнения'})}
          error={!!errors.name}
        />
        <TextField
          label="Описание"
          variant="standard"
          {...formApi.register('description', {required: 'Обязательно для заполнения'})}
          error={!!errors.description}
        />
      </StyledDialogContent>
      <Divider />
      <DialogActions>
        <Button variant="contained" onClick={documentUpdateModalClose} disabled={loading}>
          Отменить
        </Button>
        <LoadingButton variant="outlined" type="submit" disabled={loading} loading={loading}>
          Редактировать
        </LoadingButton>
      </DialogActions>
    </Form>
  );
};

const StyledDialog = styled(Dialog)({
  '& .MuiPaper-root': {
    minHeight: 280,
  },
});

const StyledDialogContent = styled(DialogContent)({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  paddingBottom: 28,
});

const StyledCircularProgress = styled(CircularProgress)({
  margin: 'auto',
});
